// Client-side mirror of server/utils/rxQuantity.js — same parser used to
// label each medicine row with the computed total (e.g. "Total: 10 tabs")
// WITHOUT round-tripping the server. Any change here must also change the
// server copy; they both feed the same dispense logic.

const SOS_DEFAULT = 10;

function parseSlot(t) {
  if (t === '½') return 0.5;
  if (t === '¼') return 0.25;
  if (t === '¾') return 0.75;
  if (t.includes('/')) {
    const [a, b] = t.split('/').map(Number);
    if (a && b) return a / b;
    return 0;
  }
  const n = Number(t);
  return isFinite(n) ? n : 0;
}

function perDay(dosageRaw) {
  if (!dosageRaw) return 1;
  const s = String(dosageRaw).trim().toUpperCase();
  if (!s) return 1;
  if (/^STAT/i.test(s)) return { stat: true };
  if (/^SOS/i.test(s)) return { sos: true };
  if (/^OD\b/.test(s)) return 1;
  if (/^(BD|BID)\b/.test(s)) return 2;
  if (/^(TDS|TID)\b/.test(s)) return 3;
  if (/^QID\b/.test(s)) return 4;
  if (/^HS\b/.test(s)) return 1;
  const slots = s.split(/[-–—|]/).map(x => x.trim()).filter(Boolean);
  if (slots.length >= 2 && slots.every(t => /^(\d+(\.\d+)?|½|¼|¾|\d+\/\d+)$/.test(t))) {
    return slots.reduce((sum, t) => sum + parseSlot(t), 0);
  }
  if (/twice|2\s*times|दो\s*बार/i.test(s)) return 2;
  if (/thrice|3\s*times|तीन\s*बार/i.test(s)) return 3;
  if (/4\s*times|चार\s*बार/i.test(s)) return 4;
  if (/once|1\s*time|एक\s*बार/i.test(s)) return 1;
  return 1;
}

function durationDays(durationRaw) {
  if (!durationRaw) return 0;
  const s = String(durationRaw).trim().toLowerCase();
  if (!s) return 0;
  if (/^\d+(\.\d+)?$/.test(s)) return Math.ceil(Number(s));
  const num = parseFloat(s);
  if (!isFinite(num) || num <= 0) return 0;
  if (/\b(day|days|din|dn|d)\b/.test(s)) return Math.ceil(num);
  if (/\b(week|weeks|hafta|haftey|haftay|wk)\b/.test(s)) return Math.ceil(num * 7);
  if (/\b(month|months|mahina|mahine|mahinay|mo)\b/.test(s)) return Math.ceil(num * 30);
  if (/\b(year|years|saal|sal|yr)\b/.test(s)) return Math.ceil(num * 365);
  return Math.ceil(num);
}

export function computeQty({ dosage, duration }) {
  const pd = perDay(dosage);
  const dd = durationDays(duration);
  if (pd && typeof pd === 'object' && pd.stat) return { qty: 1, rationale: 'STAT — single dose' };
  if (pd && typeof pd === 'object' && pd.sos) return { qty: SOS_DEFAULT, rationale: `SOS — ${SOS_DEFAULT} tabs buffer` };
  if (!dd) return { qty: SOS_DEFAULT, rationale: 'No duration — default' };
  const qty = Math.max(1, Math.ceil(pd * dd));
  const pdLabel = String(dosage).trim() || `${pd}/day`;
  const ddLabel = `${dd} day${dd === 1 ? '' : 's'}`;
  return { qty, rationale: `${pdLabel} × ${ddLabel} = ${qty}` };
}
